import { isDayKey, type DayKey } from "./grid.js";

/**
 * A date somebody typed.
 *
 * The calendar is the point of `DateField` and typing is the other half of it:
 * "12/09/2026" is four seconds of keyboard against three clicks and a month
 * step, and a form somebody fills in every week is filled in by people who
 * know the date already. The browser's own date input allowed both, and taking
 * one away to make the other bigger would be a bad trade.
 *
 * **Day first, always.** "05/06/2026" is the 5th of June in Wirral and the 6th
 * of May in California, and there is no way to tell which was meant from the
 * digits — so this reads it the way the people typing it write it, and never
 * guesses month-first. The field spells the date back out in full the moment
 * you leave it ("Friday 5 June 2026"), which is what makes that safe: a
 * misread is on screen in words before it is ever saved.
 *
 * Anything it cannot read returns null, and the field keeps the date it had
 * rather than clearing it. Half-typed input is the normal state of a box
 * somebody is typing into, and treating it as "no date" would empty a report
 * on every keystroke.
 */

const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

/** Weekday words, so the field's own spelling reads back in. */
const WEEKDAY = /^(mon|tue|tues|wed|weds|thu|thur|thurs|fri|sat|sun)[a-z]*,?\s+/;

const ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
/** `11/8/2026`, `11-08-26`, `11.8.2026`, `11 8 2026`. Day first. */
const NUMERIC = /^(\d{1,2})[/\-. ](\d{1,2})(?:[/\-. ](\d{2}|\d{4}))?$/;
/** `11 Aug 2026`, `11 August 26`, `11 august`. */
const NAMED = /^(\d{1,2})\s+([a-z]+)\.?(?:\s+(\d{2}|\d{4}))?$/;

/** A two-digit year is this century. "26" is 2026, not 1926. */
function fullYear(digits: string | undefined, fallback: number): number {
  if (digits === undefined || digits === "") return fallback;
  const value = Number(digits);
  return digits.length === 2 ? 2000 + value : value;
}

/** The month a word names, from one, or null. Three letters is enough. */
function monthFromWord(word: string): number | null {
  const found = MONTHS.findIndex(
    (month) => month === word || (word.length >= 3 && month.startsWith(word)),
  );
  return found === -1 ? null : found + 1;
}

/** `2026-08-11`, if all three parts make a real date. */
function assemble(year: number, month: number, day: number): DayKey | null {
  if (!Number.isFinite(year) || year < 1900 || year > 2999) return null;
  const key = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  // `isDayKey` is what the rest of the calendar trusts, and it refuses the
  // 31st of February rather than rolling it into March.
  return isDayKey(key) ? key : null;
}

/**
 * Read a typed date, or null.
 *
 * `contextYear` fills in a year nobody typed — "11/8" means the year of the
 * month the calendar is showing, which is the one in front of them.
 */
export function parseTypedDate(text: string, contextYear: number): DayKey | null {
  const cleaned = text.trim().toLowerCase().replace(/\s+/g, " ").replace(WEEKDAY, "");
  if (cleaned === "") return null;

  const iso = ISO.exec(cleaned);
  if (iso) return assemble(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const numeric = NUMERIC.exec(cleaned);
  if (numeric) {
    return assemble(fullYear(numeric[3], contextYear), Number(numeric[2]), Number(numeric[1]));
  }

  const named = NAMED.exec(cleaned);
  if (named) {
    const month = monthFromWord(named[2] ?? "");
    if (month === null) return null;
    return assemble(fullYear(named[3], contextYear), month, Number(named[1]));
  }

  return null;
}

/** The short form the field shows while it is being typed in. */
export function typedForm(key: DayKey): string {
  if (!isDayKey(key)) return "";
  return `${key.slice(8, 10)}/${key.slice(5, 7)}/${key.slice(0, 4)}`;
}
